import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
  orderBy,
  query,
} from "firebase/firestore";

const CATEGORIES = [
  "Garbage", "Electricity", "Motor", "Drainage", "Elevator",
  "Plumber", "Sanitary", "Carpenter", "Painter", "Security", "Other",
];

const CATEGORY_ICONS = {
  Garbage: "🗑️", Electricity: "⚡", Motor: "⚙️", Drainage: "🚰",
  Elevator: "🛗", Plumber: "🔧", Sanitary: "🪣", Carpenter: "🪚",
  Painter: "🎨", Security: "🔐", Other: "📞",
};

function categoryIcon(cat) { return CATEGORY_ICONS[cat] || "📞"; }

function categoryColor(cat) {
  const colors = {
    Garbage: "#8888a8", Electricity: "#ffa726", Motor: "#42a5f5",
    Drainage: "#26a69a", Elevator: "#ab47bc", Plumber: "#66bb6a",
    Sanitary: "#ef5350", Carpenter: "#8d6e63", Painter: "#ec407a",
    Security: "#ff7043", Other: "#6c63ff",
  };
  return colors[cat] || "#6c63ff";
}

// ── Shared styles ──────────────────────────────────────────────
const inputStyle = {
  width: "100%", background: "#0f0f13", border: "1px solid #2e2e40",
  borderRadius: "10px", padding: "10px 12px", color: "#e8e8f0",
  fontSize: "14px", outline: "none", boxSizing: "border-box",
};
const lockedStyle = {
  ...inputStyle, color: "#555570", cursor: "not-allowed", background: "#13131a",
};
const labelStyle = { fontSize: "11px", color: "#8888a8", marginBottom: "5px", display: "block" };
const lockBadge = (
  <span style={{ fontSize: "10px", color: "#555570", marginLeft: "6px" }}>🔒 locked</span>
);

// ── Multi-field input (up to 3 slots) ─────────────────────────
function MultiField({ label, values, onChange, placeholder, type = "text", locked = false, maxSlots = 3 }) {
  // values is always an array
  const slots = [...values];
  while (slots.length < 1) slots.push("");

  function updateSlot(i, val) {
    const next = [...slots];
    next[i] = val;
    onChange(next.filter((v, idx) => idx === 0 || v !== "").concat(
      next.filter((v, idx) => idx > 0 && v === "")
    ).slice(0, maxSlots));
    // simpler: just update index directly
    const arr = [...slots];
    arr[i] = val;
    onChange(arr);
  }

  function addSlot() {
    if (slots.length < maxSlots) onChange([...slots, ""]);
  }

  function removeSlot(i) {
    if (slots.length <= 1) return;
    onChange(slots.filter((_, idx) => idx !== i));
  }

  return (
    <div style={{ marginBottom: "14px" }}>
      <label style={labelStyle}>
        {label}{locked && lockBadge}
      </label>
      {slots.map((val, i) => (
        <div key={i} style={{ display: "flex", gap: "6px", marginBottom: "6px", alignItems: "center" }}>
          <input
            type={type}
            value={val}
            onChange={e => updateSlot(i, e.target.value)}
            placeholder={i === 0 ? placeholder : `${label} ${i + 1} (optional)`}
            style={locked ? lockedStyle : inputStyle}
            disabled={locked}
          />
          {!locked && slots.length > 1 && (
            <button onClick={() => removeSlot(i)} style={{
              background: "none", border: "1px solid #2e2e40", borderRadius: "8px",
              color: "#8888a8", fontSize: "14px", cursor: "pointer", padding: "6px 8px", flexShrink: 0,
            }}>✕</button>
          )}
        </div>
      ))}
      {!locked && slots.length < maxSlots && (
        <button onClick={addSlot} style={{
          background: "none", border: "1px dashed #2e2e40", borderRadius: "8px",
          color: "#555570", fontSize: "11px", cursor: "pointer", padding: "5px 10px",
          width: "100%", marginTop: "2px",
        }}>
          + Add another {label.toLowerCase()}
        </button>
      )}
    </div>
  );
}

// ── Add/Edit Modal ─────────────────────────────────────────────
function ContactModal({ contact, onClose, onDone, user, isPresident }) {
  const isEdit = !!contact;

  // Editable fields
  const [name, setName]         = useState(contact?.name || "");
  const [category, setCategory] = useState(contact?.category || "Other");
  const [notes, setNotes]       = useState(contact?.notes || "");
  const [email, setEmail]       = useState(contact?.email || "");

  // Locked-after-save fields (arrays)
  const [phones, setPhones]     = useState(
    contact?.phones?.length ? contact.phones : (contact?.phone ? [contact.phone] : [""])
  );
  const [upiIds, setUpiIds]     = useState(contact?.upiIds?.length ? contact.upiIds : [""]);

  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState("");

  // In edit mode: phones and upiIds are locked for everyone
  // EXCEPT President can add more slots if not yet at max
  const phonesLocked = isEdit;
  const upisLocked   = isEdit;
  const canAddPhone  = isEdit && isPresident && phones.filter(p => p.trim()).length < 3;
  const canAddUpi    = isEdit && isPresident && upiIds.filter(u => u.trim()).length < 3;

  async function handleSubmit() {
    if (!name.trim())                              { setErr("Name is required.");         return; }
    if (!phones.some(p => p.trim()))               { setErr("At least one phone number is required (use 0000000000 if unknown)."); return; }

    const cleanPhones = phones.map(p => p.trim()).filter(Boolean);
    const cleanUpiIds = upiIds.map(u => u.trim()).filter(Boolean);

    setSaving(true);
    try {
      if (isEdit) {
        // Only save editable fields; phones/upiIds only if President added new ones
        const updateData = {
          name: name.trim(), category, notes: notes.trim(), email: email.trim(),
        };
        if (isPresident) {
          updateData.phones = cleanPhones;
          updateData.upiIds = cleanUpiIds;
        }
        await updateDoc(doc(db, "contacts", contact.docId), updateData);
      } else {
        await addDoc(collection(db, "contacts"), {
          name: name.trim(), category, notes: notes.trim(), email: email.trim(),
          phones: cleanPhones,
          upiIds: cleanUpiIds,
          // keep legacy phone field for backward compatibility
          phone: cleanPhones[0] || "",
          addedBy: user.name, addedAt: serverTimestamp(),
        });
      }
      onDone();
    } catch (e) {
      setErr("Failed to save. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000000cc", zIndex: 1000,
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }}>
      <div style={{
        background: "#1a1a24", borderRadius: "20px 20px 0 0",
        padding: "20px", width: "100%", maxWidth: "640px",
        maxHeight: "90vh", overflowY: "auto",
      }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>
            {isEdit ? "Edit Contact" : "Add Contact"}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#8888a8", fontSize: "20px", cursor: "pointer" }}>×</button>
        </div>

        {/* Name — always editable */}
        <div style={{ marginBottom: "14px" }}>
          <label style={labelStyle}>Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="Contact name" style={inputStyle} />
        </div>

        {/* Phone numbers */}
        {phonesLocked ? (
          <div style={{ marginBottom: "14px" }}>
            <label style={labelStyle}>Phone numbers {lockBadge}</label>
            {phones.filter(p => p).map((p, i) => (
              <div key={i} style={{ marginBottom: "6px" }}>
                <input type="tel" value={p} disabled style={lockedStyle} />
              </div>
            ))}
            {canAddPhone && (
              <button onClick={() => setPhones([...phones, ""])} style={{
                background: "none", border: "1px dashed #6c63ff40", borderRadius: "8px",
                color: "#6c63ff", fontSize: "11px", cursor: "pointer", padding: "5px 10px",
                width: "100%", marginTop: "2px",
              }}>
                + Add another phone (President only)
              </button>
            )}
            {/* If President added a new slot, show it as editable */}
            {isPresident && phones.filter(p => !p).map((_, i) => {
              const idx = phones.findIndex((p, fi) => fi >= phones.filter(p => p).length);
              return (
                <div key={"new" + i} style={{ marginBottom: "6px", marginTop: "6px" }}>
                  <input type="tel"
                    value={phones[idx] || ""}
                    onChange={e => { const arr = [...phones]; arr[idx] = e.target.value; setPhones(arr); }}
                    placeholder="New phone number"
                    style={inputStyle} />
                </div>
              );
            })}
          </div>
        ) : (
          <MultiField label="Phone" values={phones} onChange={setPhones}
            placeholder="+91 XXXXX XXXXX" type="tel" maxSlots={3} />
        )}

        {/* Email — always editable */}
        <div style={{ marginBottom: "14px" }}>
          <label style={labelStyle}>Email (optional)</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)}
            placeholder="contact@example.com" style={inputStyle} />
        </div>

        {/* Category — always editable */}
        <div style={{ marginBottom: "14px" }}>
          <label style={labelStyle}>Category</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {CATEGORIES.map(c => (
              <button key={c} onClick={() => setCategory(c)} style={{
                padding: "5px 10px", borderRadius: "8px", border: "1px solid",
                borderColor: category === c ? categoryColor(c) : "#2e2e40",
                background: category === c ? `${categoryColor(c)}20` : "transparent",
                color: category === c ? categoryColor(c) : "#8888a8",
                fontSize: "11px", fontWeight: "600", cursor: "pointer",
              }}>
                {categoryIcon(c)} {c}
              </button>
            ))}
          </div>
        </div>

        {/* UPI IDs */}
        {upisLocked ? (
          <div style={{ marginBottom: "14px" }}>
            <label style={labelStyle}>UPI IDs {lockBadge}</label>
            {upiIds.filter(u => u).length === 0 && (
              <div style={{ fontSize: "12px", color: "#555570", marginBottom: "6px" }}>No UPI IDs saved</div>
            )}
            {upiIds.filter(u => u).map((u, i) => (
              <div key={i} style={{ marginBottom: "6px" }}>
                <input type="text" value={u} disabled style={lockedStyle} />
              </div>
            ))}
            {canAddUpi && (
              <button onClick={() => setUpiIds([...upiIds, ""])} style={{
                background: "none", border: "1px dashed #6c63ff40", borderRadius: "8px",
                color: "#6c63ff", fontSize: "11px", cursor: "pointer", padding: "5px 10px",
                width: "100%", marginTop: "2px",
              }}>
                + Add UPI ID (President only)
              </button>
            )}
            {isPresident && upiIds.map((u, i) => !u ? (
              <div key={"nupi" + i} style={{ marginBottom: "6px", marginTop: "6px" }}>
                <input type="text"
                  value={u}
                  onChange={e => { const arr = [...upiIds]; arr[i] = e.target.value; setUpiIds(arr); }}
                  placeholder="name@upi or phone@bank"
                  style={inputStyle} />
              </div>
            ) : null)}
          </div>
        ) : (
          <MultiField label="UPI ID" values={upiIds} onChange={setUpiIds}
            placeholder="name@upi or phone@bank" maxSlots={3} />
        )}

        {/* Notes — always editable */}
        <div style={{ marginBottom: "20px" }}>
          <label style={labelStyle}>Notes (optional)</label>
          <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="e.g. Available Mon–Sat" style={inputStyle} />
        </div>

        {err && (
          <div style={{
            background: "#ff525218", border: "1px solid #ff525240", borderRadius: "10px",
            padding: "10px 12px", marginBottom: "12px", color: "#ff5252", fontSize: "13px",
          }}>
            {err}
          </div>
        )}

        <button onClick={handleSubmit} disabled={saving} style={{
          width: "100%", padding: "14px", background: saving ? "#2e2e40" : "#6c63ff",
          border: "none", borderRadius: "12px", color: "#fff",
          fontSize: "15px", fontWeight: "700", cursor: saving ? "not-allowed" : "pointer",
        }}>
          {saving ? "Saving…" : (isEdit ? "Save Changes" : "Add Contact")}
        </button>
      </div>
    </div>
  );
}

// ── Contact Row (compact, tap-to-expand) ──────────────────────
function ContactRow({ contact, canEdit, onEdit, isLast }) {
  const [expanded, setExpanded] = useState(false);
  const color = categoryColor(contact.category);

  // Support both old single phone and new phones array
  const allPhones = contact.phones?.length ? contact.phones : (contact.phone ? [contact.phone] : []);
  const primaryPhone = allPhones[0] || "";
  const extraPhones  = allPhones.slice(1);
  const upiIds = (contact.upiIds || []).filter(Boolean);

  // Avatar initials — first letter of first two words
  const initials = contact.name
    ? contact.name.trim().split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("")
    : "?";

  // Compact name line: "Hari Krishna (Garbage Person)" truncated
  const nameLine = contact.notes
    ? `${contact.name} (${contact.notes})`
    : contact.name;

  // Short phone — last 5 digits only to save space
  const shortPhone = primaryPhone ? primaryPhone.replace(/\D/g, "").slice(-5) : "";

  return (
    <div style={{ borderBottom: isLast ? "none" : "1px solid #1e1e2e" }}>
      {/* Main row */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 12px", cursor: "pointer" }}
      >
        {/* Avatar */}
        <div style={{
          width: "32px", height: "32px", borderRadius: "50%", flexShrink: 0,
          background: `${color}20`, color, display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: "11px", fontWeight: "600",
        }}>
          {initials}
        </div>

        {/* Name (notes) — single truncated line */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: "12px", fontWeight: "500", color: "#e8e8f0",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {nameLine}
          </div>
          {contact.autoCreated && !contact.autoCreatedConfirmedBy && (
            <div style={{ fontSize: "9px", color: "#a78bfa", marginTop: "1px" }}>
              🤖 Newly Created
            </div>
          )}
        </div>

        {/* Right side — category + phone + edit */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          <span style={{
            fontSize: "9px", padding: "2px 6px", borderRadius: "4px", fontWeight: "600",
            background: `${color}18`, border: `1px solid ${color}30`, color, whiteSpace: "nowrap",
          }}>
            {contact.category}
          </span>
          {shortPhone && (
            <span style={{ fontSize: "10px", color: "#555570", fontFamily: "monospace" }}>
              {shortPhone}..
            </span>
          )}
          {canEdit && (
            <button onClick={e => { e.stopPropagation(); onEdit(contact); }} style={{
              background: "none", border: "none", color: "#555570",
              fontSize: "13px", cursor: "pointer", padding: "2px", flexShrink: 0,
            }}>✏️</button>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{
          padding: "0 14px 12px 14px", borderTop: "1px solid #1e1e2e",
          background: "#15151f",
        }}>

          {/* All phones */}
          {allPhones.length > 0 && (
            <div style={{ marginTop: "10px" }}>
              <div style={{ fontSize: "10px", color: "#555570", marginBottom: "4px" }}>Phone{allPhones.length > 1 ? "s" : ""}</div>
              {allPhones.map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                  <span style={{ fontSize: "12px", color: "#8888a8", fontFamily: "monospace" }}>{p}</span>
                  <a href={`tel:${p}`} style={{ color: "#555570", fontSize: "13px", textDecoration: "none" }}>📱</a>
                </div>
              ))}
            </div>
          )}

          {/* Email */}
          {contact.email && (
            <div style={{ marginTop: "10px" }}>
              <div style={{ fontSize: "10px", color: "#555570", marginBottom: "4px" }}>Email</div>
              <a href={`mailto:${contact.email}`} style={{ fontSize: "12px", color: "#6c63ff", textDecoration: "none" }}>
                ✉️ {contact.email}
              </a>
            </div>
          )}

          {/* UPI IDs */}
          {upiIds.length > 0 && (
            <div style={{ marginTop: "10px" }}>
              <div style={{ fontSize: "10px", color: "#555570", marginBottom: "4px" }}>UPI IDs</div>
              {upiIds.map((u, i) => (
                <div key={i} style={{ fontSize: "12px", color: "#8888a8", fontFamily: "monospace", marginBottom: "3px" }}>
                  💳 {u}
                </div>
              ))}
            </div>
          )}

          {/* Added by */}
          {contact.addedBy && (
            <div style={{ marginTop: "10px", fontSize: "10px", color: "#333348" }}>
              Added by {contact.addedBy}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Screen ────────────────────────────────────────────────
export default function ContactsScreen() {
  const { user, isMC } = useAuth();
  const canEdit    = isMC();
  const isPresident = user?.role === "President";

  const [contacts, setContacts]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showAdd, setShowAdd]       = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [search, setSearch]         = useState("");
  const [filterCat, setFilterCat]   = useState("All");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const snap = await getDocs(query(collection(db, "contacts"), orderBy("addedAt", "asc")));
        setContacts(snap.docs.map(d => ({ docId: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [refreshKey]);

  const categories = ["All", ...new Set(contacts.map(c => c.category).filter(Boolean))];

  const filtered = contacts.filter(c => {
    const matchCat = filterCat === "All" || c.category === filterCat;
    const allPhones = c.phones?.join(" ") || c.phone || "";
    const matchSearch = !search ||
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      allPhones.includes(search) ||
      c.email?.toLowerCase().includes(search.toLowerCase()) ||
      c.notes?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  if (loading) {
    return <div style={{ textAlign: "center", padding: "60px 20px", color: "#555570", fontSize: "13px" }}>Loading contacts…</div>;
  }

  return (
    <div style={{ padding: "16px", maxWidth: "640px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
        <div>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "#e8e8f0" }}>📞 IMP Contacts</div>
          <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
            Society service contacts · {contacts.length} saved
          </div>
        </div>
        <button onClick={() => setShowAdd(true)} style={{
          background: "#6c63ff", border: "none", borderRadius: "10px",
          padding: "8px 14px", color: "#fff", fontSize: "13px", fontWeight: "600", cursor: "pointer",
        }}>
          + Add
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: "12px" }}>
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍  Search contacts…"
          style={{
            width: "100%", background: "#1a1a24", border: "1px solid #2e2e40",
            borderRadius: "12px", padding: "10px 14px", color: "#e8e8f0",
            fontSize: "13px", outline: "none", boxSizing: "border-box",
          }}
        />
      </div>

      {/* Category filter */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "16px", overflowX: "auto", paddingBottom: "4px" }}>
        {categories.map(cat => (
          <button key={cat} onClick={() => setFilterCat(cat)} style={{
            flexShrink: 0, padding: "5px 10px", borderRadius: "8px", border: "1px solid",
            borderColor: filterCat === cat ? "#6c63ff" : "#2e2e40",
            background: filterCat === cat ? "#6c63ff20" : "transparent",
            color: filterCat === cat ? "#6c63ff" : "#8888a8",
            fontSize: "11px", fontWeight: "600", cursor: "pointer",
          }}>
            {cat !== "All" && categoryIcon(cat) + " "}{cat}
          </button>
        ))}
      </div>

      {/* Contact list */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#555570", fontSize: "13px" }}>
          No contacts found.
        </div>
      ) : (
        <div style={{ background: "#1a1a24", border: "1px solid #2e2e40", borderRadius: "12px", overflow: "hidden" }}>
          {filtered.map((contact, idx) => (
            <ContactRow
              key={contact.docId}
              contact={contact}
              canEdit={canEdit}
              onEdit={setEditContact}
              isLast={idx === filtered.length - 1}
            />
          ))}
        </div>
      )}

      <div style={{ fontSize: "11px", color: "#2e2e40", textAlign: "center", marginTop: "12px", padding: "8px" }}>
        Anyone can add · MC can edit name/email/notes · Phones &amp; UPI locked after save · No delete
      </div>

      {/* Modals */}
      {showAdd && (
        <ContactModal
          user={user}
          isPresident={isPresident}
          onClose={() => setShowAdd(false)}
          onDone={() => { setShowAdd(false); setRefreshKey(k => k + 1); }}
        />
      )}
      {editContact && canEdit && (
        <ContactModal
          contact={editContact}
          user={user}
          isPresident={isPresident}
          onClose={() => setEditContact(null)}
          onDone={() => { setEditContact(null); setRefreshKey(k => k + 1); }}
        />
      )}
    </div>
  );
}
